// Portfolio Optimizer API Client
// Interacts with the backend portfolio optimizer API

interface OptimizationResult {
  optimal_weights: Record<string, number>;
  expected_return: number;
  expected_volatility: number;
  sharpe_ratio: number;
  constraints_satisfied: boolean;
  constraint_violations: string[];
  optimization_time: number;
  convergence_status: string;
}

interface CalculatePositionSizesRequest {
  optimal_weights: Record<string, number>;
  portfolio_value: number;
  risk_tolerance: "CONSERVATIVE" | "MODERATE" | "AGGRESSIVE";
}

interface PositionSizesResponse {
  position_sizes: Record<string, number>;
  total_value: number;
  risk_adjusted_sizes: Record<string, number>;
}

interface RebalancingTriggersRequest {
  current_weights: Record<string, number>;
  optimal_weights: Record<string, number>;
}

interface RebalancingTriggersResponse {
  triggers: {
    asset: string;
    current_weight: number;
    optimal_weight: number;
    deviation: number;
    trigger_threshold: number;
    should_rebalance: boolean;
  }[];
  overall_deviation: number;
  rebalance_required: boolean;
}

interface BacktestStrategyRequest {
  strategy: string;
  historical_data: {
    dates: string[];
    prices: Record<string, number[]>;
  };
  config: {
    initial_investment: number;
    rebalance_frequency: string;
    transaction_costs?: number;
  };
}

interface BacktestResult {
  performance_metrics: {
    total_return: number;
    annualized_return: number;
    volatility: number;
    sharpe_ratio: number;
    max_drawdown: number;
  };
  portfolio_values: number[];
  trades: {
    date: string;
    asset: string;
    action: string;
    amount: number;
  }[];
}

class PortfolioOptimizerApiClient {
  private baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || import.meta.env.VITE_PORTFOLIO_OPTIMIZER_URL || 'http://localhost:8001';
  }

  private async fetchWithRetry(url: string, options: RequestInit, retries = 3): Promise<Response> {
    let lastError: Error;

    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.status >= 400 && response.status < 600) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return response;
      } catch (error) {
        lastError = error as Error;

        if (attempt < retries - 1) {
          const delay = Math.pow(2, attempt) * 1000; // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    throw lastError!;
  }

  async optimizePortfolio(strategy: string, optimization_data: Record<string, any>): Promise<OptimizationResult> {
    const url = `${this.baseUrl}/optimize`;
    const response = await this.fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ strategy, optimization_data }),
    });

    return response.json();
  }


  async calculatePositionSizes(
    optimalWeights: Record<string, number>,
    portfolioValue: number,
    riskTolerance: number
  ): Promise<PositionSizesResponse> {
    const url = `${this.baseUrl}/position-sizes`;
    const response = await this.fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        optimal_weights: optimalWeights,
        portfolio_value: portfolioValue,
        risk_tolerance: riskTolerance,
      }),
    });

    return response.json();
  }

  async getRebalancingTriggers(
    currentWeights: Record<string, number>,
    optimalWeights: Record<string, number>
  ): Promise<RebalancingTriggersResponse> {
    const url = `${this.baseUrl}/rebalancing-triggers`;
    const response = await this.fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        current_weights: currentWeights,
        optimal_weights: optimalWeights,
      }),
    });

    return response.json();
  }

  async backtestStrategy(
    strategy: string,
    historicalData: BacktestStrategyRequest['historical_data'],
    config: BacktestStrategyRequest['config']
  ): Promise<BacktestResult> {
    const url = `${this.baseUrl}/backtest`;
    const response = await this.fetchWithRetry(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        strategy,
        historical_data: historicalData,
        config,
      }),
    });

    return response.json();
  }
}

export { PortfolioOptimizerApiClient };
export type {
  OptimizationResult,
  CalculatePositionSizesRequest,
  PositionSizesResponse,
  RebalancingTriggersRequest,
  RebalancingTriggersResponse,
  BacktestStrategyRequest,
  BacktestResult,
};